import { FC, Fragment, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Descriptions,
  DescriptionsProps,
  Flex,
  Modal,
  ModalProps,
  Spin,
  Tag,
  Table,
  notification,
  Form,
  Input,
} from "antd";
import { ColumnsType } from 'antd/es/table';
import { ChangeEnum, ConfirmedEnum, OperateEnum } from "@/types/inventory";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName, hasPermission } from "@/utils/user";
import dayjs from "dayjs";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from 'react-i18next';
import useDataDetail from "@/hooks/useDataDetail";
import { useSelector } from "react-redux";
import { RootState } from "@/store/index";
import { User, UserPermissionEnum } from "@/types/user";

import { dateFormat } from "@/utils/format";
import { orderFetch, orderDel, orderItemRms, orderDescUpdate } from "@/apis/inventoryv2";
import { OrderV2, OrderItemV2, OrderItemLocation } from "@/types/inventoryV2";
import { confirmTypes, opTypes, changeTypes, getLocationCode } from "@/pages/Warehouse/utils";
import SText from '@/styles/SText';
import PreviewLocationCode from '@/pages/Warehouse/components/PreviewLocationCode';
import PrintInventoryV2, { PrintType } from "@/SupplierPages/Home/InventoryOrder/components/PrintInventoryV2";
import ReturnModalForm from "@/pages/Warehouse/components/ReturnModalForm";
import useFrontDelTip from "@/hooks/useFrontDelTip";
import PrintReturn from "../../../Inventory/components/PrintReturn";
import PrintQC from "@/SupplierPages/Home/InventoryOrder/components/PrintQC";
import { getLocationIdx } from "@/utils/wLocation";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import convertTo2DArray from "@/utils/convert2double";
import _ from "lodash";
import { DescriptionsItemType } from "antd/es/descriptions";
import DetailModal from "@/components/DetailModal";
import { numFormmater } from "@/pages/Requirement/util";
import PrintSaleIn from "@/SupplierPages/Home/InventoryOrder/components/PrintSaleIn";
import ListFilterBar from "@/components/ListFilterBar";
import { filterProductItemsByKeyword } from "@/utils/product";
import { MaterialSearchWrap } from "./style";

interface OrderDetailProps extends ModalProps {
  id?: string;
  data?: OrderV2;
  noModal?: boolean;
  handleCancel: (order: OrderV2 | null, type?: "ADD" | "UPDATE" | "DEL") => void;
  handleRm: (id: string) => void;
  readonly?: boolean;
}

// 批量删除订单项时每批的大小
const BATCH_DELETE_SIZE = 20;

const OrderDetailV2: FC<OrderDetailProps> = ({ id, data, noModal = false, handleCancel, handleRm, readonly = false, ...modalProps }) => {
  const { t } = useTranslation();
  const curUser: User = useSelector((state: RootState) => state.userReducer.curUser);

  const { loading: deLoading, fetchDetail: fetch } = useDataDetail<OrderV2, [string]>({ api: orderFetch })
  const [returnOpen, setReturnOpen] = useState<boolean>(false);
  const [previewBarcodes, setPreviewBarcodes] = useState<{
    visible: boolean;
    locations: OrderItemLocation[];
    productName: string;
  }>({
    visible: false,
    locations: [],
    productName: ''
  });

  const [detail, setDetail] = useState<OrderV2 | null>(null);
  const [keyword, setKeyword] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const filteredItems = useMemo(() => filterProductItemsByKeyword(detail?.items || [], searchKeyword), [detail?.items, searchKeyword]);
  const searchOrderId = id || data?.id;

  useEffect(() => {
    setKeyword("");
    setSearchKeyword("");
  }, [searchOrderId]);
  const [prting, setPrting] = useState<boolean>(false);
  const [QCPrting, setQCPrting] = useState<boolean>(false);
  const [salePrting, setSalePrting] = useState<boolean>(false);
  const [descLoading, setDescLoading] = useState<boolean>(false);
  const [desc, setDesc] = useState<string>('');


  const opType = opTypes.find(item => item.value === detail?.opType);
  const confirmType = confirmTypes.find(item => item.value === detail?.confirmState);
  const orderType = changeTypes.find((item) => item.value === detail?.type);
  const { onConfirm: onDel } = useFrontDelTip({
    cb: () => Promise.resolve(true),
    desc: t('order.delTip1', { type: orderType?.label }),
    message: t('common.tip'),
  });
  useEffect(() => {
    if (id) {
      fetch(id).then(res => {
        setDetail(res);
        setDesc(res.desc)
      })
        .catch(err => {
          notification.error({ message: t('counting.loadFailed') })
        })
    } else {
      setDesc(data?.desc ?? '')
      setDetail(data || null)
    }
  }, [id, data]);

  const productNum = useMemo(() => {
    let ids: Array<string> = [];
    if (detail?.items) {
      detail.items.forEach(item => {
        if (!!!ids.includes(item.productId)) ids.push(item.productId)
      })
    }
    return ids.length
  }, [detail?.items])

  const descriptionItems: DescriptionsProps["items"] = [
    {
      label: t('order.operator'),
      children: detail && (
        !!!detail?.updater ? '-' : <>
          <WMSAvatar
            uniChar={detail?.updater?.id ?? "1"}
            avatarId={detail?.updater?.avatar ?? ""}
            creator={detail?.updater}
            alt={detail?.updater ? getUserAvatarTip(detail?.updater) : ''}
            size={24}
          />
          <span>{getUserName(detail?.updater)}</span>
        </>
      ),
      span: 1,
    },
    {
      label: t('order.oprtTime'),
      children: detail && dateFormat(dayjs(detail?.updatedAt), true),
      span: 1,
    },
    {
      label: t('order.operationSource'),
      span: 1,
      children: detail && (
        <Tag key={opType?.label} color={opType?.colorKey}>{`${opType?.label ?? ""
          }`}</Tag>
      ),
    },
    {
      label: t('order.orderType'),
      span: 1,
      children: detail && (
        <Tag key={orderType?.label} color={orderType?.colorKey}>{`${orderType?.label ?? ""
          }`}</Tag>
      ),
    },
    {
      label: t('order.printCount'),
      span: 1,
      children: detail && (detail?.printCount || '-'),
    }
  ];

  if (detail?.type && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(detail.type)) {
    descriptionItems.splice(1, 0, {
      label: t('warehouse.arrivalAt'),
      children: detail?.arrivalAt ? dateFormat(dayjs(detail.arrivalAt), true) : '-',
      span: 1,
    },
    )
  }

  const onCancelClick = () => {
    handleCancel(null);
  }

  // 定义订单项表格列
  const itemColumns: ColumnsType<OrderItemV2> = [
    {
      title: t('purchase.productInfo'),
      fixed: 'left',
      children: [
        {
          title: <SText text={t('product.code')} />,
          key: 'productCode',
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.code ?? "";
            const valB = b?.product?.code ?? "";
            return valA.localeCompare(valB, undefined, { numeric: true });
          },
          render: (_, record) => <SText text={record.product?.code || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.name')} />,
          key: 'productName',
          width: 150,
          render: (_, record) => <SText text={record.product?.name || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.model')} />,
          key: 'productModel',
          width: 120,
          render: (_, record) => <SText text={record.product?.model || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('product.uom')} />,
          key: 'uom',
          width: 60,
          align: 'center',
          render: (_, record) => <SText text={record.product?.uom || ''} style={{ width: "100%" }} />
        },
        {
          title: t("product.materialProcess"),
          key: "process",
          width: 120,
          sorter: (a, b) => {
            const valA = a?.product?.process ?? "";
            const valB = b?.product?.process ?? "";
            return valA.localeCompare(valB);
          },
          render: (_, record) => <SText text={record?.product?.process ?? ''} />,
        },
      ]
    },
    {
      title: <SText text={t('warehouse.batchNo')} />,
      dataIndex: 'lot',
      key: 'lot',
      width: 120,
      render: (text) => <SText text={text || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.supplierBatchNo')} />,
      dataIndex: 'thdLot',
      key: 'thdLot',
      width: 120,
      render: (text) => <SText text={text || ''} style={{ width: "100%" }} />
    },
    {
      title: <SText text={t('warehouse.location')} />,
      key: 'locations',
      width: 250,
      render: (_, record) => {
        if (!record.locations || record.locations.length === 0) {
          return '-';
        }
        return <Flex wrap>
          {record.locations.map(loc => (
            <Tag>{getLocationIdx(loc.location)} ({loc.count})</Tag>
          ))}
        </Flex>
      }
    },
    {
      title: t('warehouse.productOldCount'),
      key: 'productOldCount',
      width: 150,
      sorter: (a, b) => {
        const valA = a?.productOldCount ?? 0;
        const valB = b?.productOldCount ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.productOldCount !== null ? numFormmater(record.productOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.productNewCount'),
      key: 'productNewCount',
      width: 150,
      sorter: (a, b) => {
        const valA = a?.productNewCount ?? 0;
        const valB = b?.productNewCount ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.productNewCount !== null ? numFormmater(record.productNewCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemOldCount'),
      key: 'lotItemOldCount',
      width: 150,
      sorter: (a, b) => {
        const valA = a?.lotItemOldCount ?? 0;
        const valB = b?.lotItemOldCount ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.lotItemOldCount !== null ? numFormmater(record.lotItemOldCount) : ''} />
      )
    },
    {
      title: t('warehouse.lotItemNewCount'),
      key: 'lotItemNewCount',
      width: 150,
      sorter: (a, b) => {
        const valA = a?.lotItemNewCount ?? 0;
        const valB = b?.lotItemNewCount ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.lotItemNewCount !== null ? numFormmater(record.lotItemNewCount) : ''} />
      )
    },
    {
      title: t('purchase.otherInfo'),
      children: [
        {
          title: <SText text={t('purchase.originalBrand')} />,
          key: 'originalBrand',
          width: 120,
          render: (_, record) => <SText text={record.ext?.originalBrand || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.originalCode')} />,
          key: 'originalCode',
          width: 120,
          render: (_, record) => <SText text={record.ext?.originalCode || ''} style={{ width: "100%" }} />
        },
        // {
        //   title: <SText text={t('purchase.dcc')} />,
        //   key: 'shelfLifeDate',
        //   width: 110,
        //   render: (_, record) => <SText text={String(record.ext?.dcc || '')} style={{ width: "100%" }} />
        // },
        {
          title: <SText text={t('purchase.manufactureDate')} />,
          key: 'manufactureDate',
          width: 120,
          render: (_, record) => <SText text={record.ext?.manufactureDate ? dateFormat(dayjs(record.ext.manufactureDate), true) : ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.msdLevel')} />,
          key: 'msdLevel',
          width: 100,
          render: (_, record) => <SText text={record.ext?.msdLevel || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.originPlace')} />,
          key: 'originPlace',
          width: 100,
          render: (_, record) => <SText text={record.ext?.originPlace || ''} style={{ width: "100%" }} />
        },
        {
          title: <SText text={t('purchase.netWeight')} />,
          key: 'netWeight',
          width: 80,
          render: (_, record) => <SText text={record.ext?.netWeight ? `${record.ext.netWeight} g` : ''} style={{ width: "100%" }} />
        },
      ]
    },
    {
      title: t('purchase.quantity'),
      dataIndex: 'count',
      key: 'count',
      width: 80,
      fixed: 'right',
      align: "center",
      sorter: (a, b) => {
        const valA = a?.count ?? 0;
        const valB = b?.count ?? 0;
        return valA - valB;
      },
      render: (_, record) => (
        <SText text={record.count ? numFormmater(record.count) : ''} />
      )
    },
    {
      title: t('common.opt'),
      key: 'operation',
      width: 120,
      fixed: 'right',
      render: (_, record) => {
        return (
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => {
              setPreviewBarcodes({
                visible: true,
                locations: record.locations || [],
                productName: record.product?.name || ''
              });
            }}
          >
            {t('warehouse.viewBarcode')}
          </Button>
        );
      }
    },
  ];

  const handleDel = () => {

    const itemIds: Array<string> = detail?.items?.length ? detail?.items?.map((item) => item.id) : [];

    onDel().then(() => {
      return batchDeleteOrderItems(detail?.id ?? '', itemIds)
    }).then(() => {
      return orderDel(detail?.id ?? '');
    })
      .then(() => {
        notification.success({ placement: 'top', message: t('common.rmSuccess') });
        handleCancel(detail ?? null, "DEL");
      }).catch((err) => {
        notification.error({ placement: 'top', message: t('common.rmError') });
      })
  }

  const saveDesc = () => {
    setDescLoading(true);
    orderDescUpdate(detail?.id ?? '', desc)
      .then(() => {
        notification.success({ message: t('common.saveOk') })
        detail && setDetail({ ...detail, desc })
      })
      .finally(() => setDescLoading(false))
  }

  const handlePrinted = (printCount: number) => {
    setDetail(current => current ? { ...current, printCount } : current);
  }

  // 批量删除订单项
  const batchDeleteOrderItems = async (orderId: string, itemIds: Array<string>): Promise<void> => {
    if (itemIds.length === 0) return;

    const batches = convertTo2DArray(itemIds, BATCH_DELETE_SIZE);

    for (const batchIds of batches) {
      await orderItemRms(orderId, batchIds);
    }
  };

  const Content = (
    <Fragment>
      {deLoading && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Spin /></div>}
      {detail && !deLoading &&
        <Fragment>
          <Descriptions bordered items={descriptionItems} column={4} />
          <Form.Item label={t('purchase.remark')}>
            <Flex gap={6}>
              <Input.TextArea readOnly={detail.confirmState === ConfirmedEnum.CONFIRM} size="small" autoSize={{ minRows: 1, maxRows: 1 }} maxLength={200} value={desc} onChange={e => setDesc(e.target.value)} placeholder={t('requirement.plInventoryDesc')} />
              {detail.confirmState !== ConfirmedEnum.CONFIRM && <Button loading={descLoading} onClick={saveDesc} type="primary" disabled={desc === detail.desc}>{t('common.save')}</Button>}
            </Flex>
          </Form.Item>
          <div style={{ flex: 1, width: "100%", overflow: 'hidden' }}>
            <STableLayout
              headerChildren={null}
              columns={itemColumns as Array<AnyObject>}
              dataSource={filteredItems}
              rowKey="id"
              pagination={false}
              scrollX={200}
              bordered
              size="small"
              total={detail?.items?.length || 0}
              headerSub={<>
                <SText text={`${t('warehouse.productNum')}:${productNum}`} />
                <MaterialSearchWrap>
                  <ListFilterBar onSearch={() => setSearchKeyword(keyword)} onReset={() => { setKeyword(""); setSearchKeyword(""); }} showReset={false}>
                    {({ submit }) => (
                      <Input allowClear value={keyword} onChange={event => setKeyword(event.target.value)} onPressEnter={submit}
                        placeholder={`${t("product.code")}/${t("product.name")}/${t("product.model")}`}
                        aria-label={t("requirement.searchPro")} />
                    )}
                  </ListFilterBar>
                </MaterialSearchWrap>
              </>}
            />
          </div>
        </Fragment>
      }
      {/* 条码预览弹窗 */}
      <PreviewLocationCode
        visible={previewBarcodes.visible}
        onClose={() => setPreviewBarcodes({ visible: false, locations: [], productName: '' })}
        locationBarcodes={previewBarcodes.locations}
        title={`${t('warehouse.viewBarcode')} - ${previewBarcodes.productName}`}
      />
    </Fragment>
  )

  return (
    !noModal ?
      <Fragment>
        <DetailModal
          logBookId={detail?.logBookId}
          modalProps={{
            width: "100vw",
            height: "100vh",
            styles: {
              content: { height: "100vh" },
              body: { height: "calc(100vh - 60px)", padding: 0 }
            },
            mask: false,
            title: (
              <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -4 }}>
                <div>
                  {detail?.number}
                  <Tag key={opType?.label} color={opType?.colorKey} style={{ marginLeft: 4 }}>{`${opType?.label ?? ""}`} {`${orderType?.label ?? ""}`}</Tag>
                </div>
                <Flex gap={10} align="center" justify='end' style={{ marginRight: 30 }}>
                  <Tag key={confirmType?.label} color={confirmType?.colorKey} >{`${confirmType?.label ?? ""}`}</Tag>
                  {detail?.confirmState === ConfirmedEnum.CONFIRM &&
                    (hasPermission(curUser, [UserPermissionEnum.ADMIN]) ||
                      (hasPermission(curUser, [UserPermissionEnum.STORE_WAREHOUSE_MANAGEMENT]) &&
                        hasPermission(curUser, [UserPermissionEnum.ACT_STORE_IN]) &&
                        hasPermission(curUser, [UserPermissionEnum.ACT_STORE_OUT]))) &&
                    <Button onClick={() => setReturnOpen(true)}>{t('warehouse.returnStock')}</Button>
                  }
                  {!readonly && detail?.confirmState === ConfirmedEnum.NOTCONFIRM && <Button type="text" danger onClick={handleDel}>{t('common.del')}</Button>}
                  {detail && ![OperateEnum.IN_RETURN, OperateEnum.OUT_RETURN].includes(detail.opType) && <PrintInventoryV2 selectedRows={detail.items ? detail.items.map(itm => ({ ...itm, order: { ...detail, items: [] } })) : []}
                    disabled={prting || deLoading} onStart={() => setPrting(true)} onEnd={() => setPrting(false)} onPrinted={handlePrinted} showBottom
                    printType={[ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(detail.type) ? PrintType.OUT : PrintType.NORMAL}
                  />}
                  {!readonly && detail && [OperateEnum.IN_RETURN, OperateEnum.OUT_RETURN].includes(detail.opType) && <PrintReturn order={detail}
                    disabled={prting || deLoading} onStart={() => setPrting(true)} onEnd={() => setPrting(false)} onPrinted={handlePrinted}
                  />}
                  {detail && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(detail.type) && <PrintQC selectedRows={detail.items ? detail.items.map(itm => ({ ...itm, order: { ...detail, items: [] } })) : []}
                    disabled={QCPrting || deLoading} onStart={() => setQCPrting(true)} onEnd={() => setQCPrting(false)}
                    printType={PrintType.SUP}
                  />}
                  {
                    detail && detail.opType === OperateEnum.PROD_IN && <PrintSaleIn order={detail} disabled={deLoading || salePrting} onStart={() => setSalePrting(true)} onEnd={() => setSalePrting(false)} />
                  }
                </Flex>
              </div>
            ),
            onCancel: onCancelClick,
            footer: null,
            ...modalProps,
          }}

        >
          <Flex vertical style={{ width: "100%", height: "100%" }}>
            {Content}
          </Flex>
        </DetailModal>
        {returnOpen && detail && <ReturnModalForm detail={detail} onCancel={() => setReturnOpen(false)}
          onSuccess={handleCancel} />}
      </Fragment>
      : Content
  );
};

export default OrderDetailV2;
